import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";

import { formatDate } from "../lib/format";
import { shopifyOrderNumber } from "../lib/shopifyStore";
import { useTRPC } from "../lib/trpc";
import { useShopifyShop } from "../lib/useShopifyShop";
import { useTenantRole } from "../lib/useTenantRole";
import { ErrorAlert } from "./ErrorAlert";
import { ExpandableRow } from "./ExpandableRow";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { ShopifyCustomerLink, ShopifyOrderLink } from "./ShopifyLinks";
import { ShopifyPurchaseDetail } from "./ShopifyPurchaseDetail";
import { TableMessageRow } from "./TableMessageRow";

const COLUMNS = 9;

/**
 * The $0 Shopify order each claim ships as, with a retry for an order Shopify refused. A row
 * opens in place to the full detail of the purchase it ships.
 */
export function ShopifyOrdersCard() {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { shopHandle } = useShopifyShop();
  const [expanded, setExpanded] = useState<string | null>(null);
  const shipOrders = useQuery(trpc.shopify.shipOrders.queryOptions());
  const retryDispatch = useMutation(
    trpc.shopify.retryDispatch.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.pathKey() });
        if (data.dispatchStatus === "created") toast.success("Order created in Shopify.");
        else toast.error(data.lastError ?? "Shopify refused the order again.");
      },
    }),
  );

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>Orders</CardTitle>
        <CardDescription>
          The $0 Shopify order each claim ships as. A refused order is retried every five minutes
          and waits for a manual retry after ten failures.
        </CardDescription>
      </CardHeader>
      {/* A container, so an open row's detail can take the card's width and not the table's. */}
      <CardContent className="@container flex flex-col gap-2">
        {shipOrders.error && <ErrorAlert message={shipOrders.error.message} />}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Shopify order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Dispatch</TableHead>
              <TableHead>Fulfillment</TableHead>
              <TableHead>Tracking</TableHead>
              <TableHead>Last error</TableHead>
              <TableHead>Created</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(shipOrders.data ?? []).length === 0 && (
              <TableMessageRow colSpan={COLUMNS}>No orders yet.</TableMessageRow>
            )}
            {(shipOrders.data ?? []).map((o) => {
              const orderNumber = o.shopifyOrderGid ? shopifyOrderNumber(o.shopifyOrderGid) : null;
              return (
                <ExpandableRow
                  key={o.id}
                  detailId={`shopify-ship-order-${o.id}`}
                  label={`Show ${orderNumber ? `order #${orderNumber}` : "ship order"}`}
                  isOpen={expanded === o.id}
                  onToggle={() => setExpanded(expanded === o.id ? null : o.id)}
                  colSpan={COLUMNS}
                  detail={
                    <ShopifyPurchaseDetail
                      tenantId={tenantId}
                      purchaseId={o.purchaseId}
                      shopHandle={shopHandle}
                    />
                  }
                >
                  <TableCell className="font-mono text-xs">
                    {o.shopifyOrderGid && orderNumber ? (
                      <ShopifyOrderLink shopHandle={shopHandle} orderGid={o.shopifyOrderGid}>
                        #{orderNumber}
                      </ShopifyOrderLink>
                    ) : (
                      "not created"
                    )}
                  </TableCell>
                  <TableCell>
                    <ShopifyCustomerLink tenantId={tenantId} row={o} />
                  </TableCell>
                  <TableCell>
                    <Badge variant={o.dispatchStatus === "failed" ? "destructive" : "secondary"}>
                      {o.dispatchStatus.replaceAll("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <OrderStatusBadge status={o.orderStatus} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{o.trackingNumber ?? "—"}</TableCell>
                  <TableCell className="max-w-64 truncate text-muted-foreground">
                    {o.lastError ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(o.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    {can("admin") &&
                      (o.dispatchStatus === "out_of_stock"
                        ? o.shopifyAnnotatedAt === null
                        : o.dispatchStatus !== "created") && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={retryDispatch.isPending}
                          onClick={() => retryDispatch.mutate({ id: o.id })}
                        >
                          Retry
                        </Button>
                      )}
                  </TableCell>
                </ExpandableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
